import { useCallback, useEffect, useRef } from 'react';

import { useHasAgentConversationStarted } from 'state/hasAgentConversationStarted';
import { AuthEvent } from 'genesys/auth-plugin';
import { MessagingServiceEvent } from 'genesys/messaging-service-plugin';
import { sendMessage } from 'genesys/genesys';
import { waitForMessagingServiceStarted } from 'genesys/waitForMessagingServiceStarted';
import { DefinedError, useSetGenesysError } from 'genesys/error/error-state';

import { useLastSentToAgentMessage } from './useLastSentToAgentMessage';
import { useSubscribe } from './useSubscribe';
import { useIsGenesysReAuthenticating } from './useIsGenesysReAuthenticating';

/**
 * useHandleGenesysReAuthentication
 * During conversation with agent, the token that Genesys uses for authentication might expire (after an hour),
 * in that case, Genesys SDK need to fetch a new token and do the authentication again. This hook handles that
 * re-authenticating process.
 */
export const useHandleGenesysReAuthentication = () => {
  const timeoutPromiseRef = useRef(null);

  const { isGenesysReAuthenticating, setIsGenesysReAuthenticating } =
    useIsGenesysReAuthenticating();

  const hasAgentConversationStarted = useHasAgentConversationStarted();

  const { lastSentToAgentMessage } = useLastSentToAgentMessage();

  const setGenesysError = useSetGenesysError();

  const handleAuthenticatingEvent = useCallback(() => {
    setIsGenesysReAuthenticating(true);

    /*
     * we don't want timeout waiting happens in parallel, we need to wait for the firt
     * one to complete first.
     */
    if (!timeoutPromiseRef.current) {
      timeoutPromiseRef.current = waitForMessagingServiceStarted(30000);

      timeoutPromiseRef.current.then(
        () => {
          timeoutPromiseRef.current = null;
        },
        () => {
          timeoutPromiseRef.current = null;

          setIsGenesysReAuthenticating(false);
          setGenesysError(DefinedError.InConversationError);
        },
      );
    }
  }, [setIsGenesysReAuthenticating, setGenesysError]);

  const handleConversationRestarted = useCallback(
    (topic, data) => {
      setIsGenesysReAuthenticating(false);

      if (data?.data?.newSession === false) {
        sendMessage({
          message: lastSentToAgentMessage,
        });
      }
    },
    [setIsGenesysReAuthenticating, lastSentToAgentMessage],
  );

  const handleAuthenticationError = useCallback(() => {
    setIsGenesysReAuthenticating(false);

    setGenesysError(DefinedError.InConversationError);
  }, [setIsGenesysReAuthenticating, setGenesysError]);

  useEffect(() => {
    if (!hasAgentConversationStarted) {
      setIsGenesysReAuthenticating(false);
    }
  }, [hasAgentConversationStarted, setIsGenesysReAuthenticating]);

  useSubscribe(
    AuthEvent.Authenticating,
    handleAuthenticatingEvent,
    hasAgentConversationStarted,
  );

  useSubscribe(
    MessagingServiceEvent.Started,
    handleConversationRestarted,
    isGenesysReAuthenticating,
  );

  useSubscribe(
    AuthEvent.AuthError,
    handleAuthenticationError,
    isGenesysReAuthenticating,
  );

  useSubscribe(
    AuthEvent.TokenError,
    handleAuthenticationError,
    isGenesysReAuthenticating,
  );

  useSubscribe(
    AuthEvent.Error,
    handleAuthenticationError,
    isGenesysReAuthenticating,
  );

  useSubscribe(
    AuthEvent.AuthProviderError,
    handleAuthenticationError,
    isGenesysReAuthenticating,
  );
};
